import { Button, cx } from "@light-technology/components";
import { useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  YAxis,
} from "recharts";
import { api } from "../../state/api";
import {
  CHART_COLORS,
  CHART_CURSOR_COLOR,
  CHART_RIGHT_MARGIN_PX,
  CHART_TOP_MARGIN_PX,
  CHART_Y_AXIS_PADDING,
  ENROLLMENTS_DEFAULT_DAYS,
  hasNoChartData,
} from "../../utilities/analyticsHelpers";
import { ChartCard } from "./ChartCard";
import { FullHeightTooltipCursor, HorizontalGridLine } from "./ChartLines";
import { ChartTooltip } from "./ChartTooltip";
import { NoPlanState } from "./NoPlanState";
import "./charts.css";

type EnrollmentsLineChartProps = {
  appUUID: string;
  appSlug: string;
  days?: number;
  className?: string;
};

export function EnrollmentsLineChart({
  appUUID,
  appSlug,
  days = ENROLLMENTS_DEFAULT_DAYS,
  className,
}: EnrollmentsLineChartProps) {
  const [focusedLine, setFocusedLine] = useState<string | null>(null);

  const {
    data: chartResponse,
    isLoading,
    error,
  } = api.useGetEnrollmentsChartQuery({
    appUUID,
    days,
  });

  const hasError = !!error;

  const data = chartResponse?.data ?? [];

  const planNames = chartResponse?.plan_names ?? [];

  const noData = !isLoading && !hasError && hasNoChartData(data, planNames);

  return (
    <ChartCard
      title="Weekly enrollments"
      hasError={hasError}
      className={cx("flex flex-col", className)}
      action={
        !noData &&
        !hasError && (
          <Button
            color="link-gray"
            size="sm"
            href={`/apps/${appSlug}/plans`}
            className="font-mono"
          >
            View plans
          </Button>
        )
      }
      isFullHeightChart
    >
      {isLoading ? (
        <div className="mt-3xl flex min-h-62 flex-1 items-center justify-center">
          <div className="h-full w-full animate-pulse rounded bg-secondary" />
        </div>
      ) : noData ? (
        <NoPlanState hasPlans={planNames.length > 0} days={days} />
      ) : (
        <div className="flex flex-1 flex-col [&_*]:!outline-none [&_*:focus]:!outline-none">
          <ResponsiveContainer
            className="-ml-[8%] min-h-62 w-[108%]"
            width="108%"
            height="100%"
          >
            <LineChart
              data={data}
              margin={{
                top: CHART_TOP_MARGIN_PX,
                right: CHART_RIGHT_MARGIN_PX,
                left: 0,
                bottom: 2,
              }}
            >
              <CartesianGrid
                strokeDasharray="0 0"
                className="[:last-child]:hidden"
                stroke="var(--color-border-tertiary)"
                horizontal={HorizontalGridLine}
                vertical={false}
              />
              <YAxis
                domain={[
                  0,
                  (dataMax: number) => dataMax * CHART_Y_AXIS_PADDING,
                ]}
                hide
              />
              <Tooltip
                content={
                  <ChartTooltip
                    planNameColorOrder={planNames}
                    focusedItem={focusedLine}
                  />
                }
                cursor={
                  <FullHeightTooltipCursor
                    strokeDasharray="3 3"
                    stroke={CHART_CURSOR_COLOR}
                  />
                }
              />
              {(() => {
                const names = planNames;
                return names
                  .slice()
                  .reverse()
                  .map((planName: string, index: number) => {
                    const colorIndex = index % CHART_COLORS.length;
                    const isFocused =
                      focusedLine === planName || focusedLine === null;
                    const hasEnrollments = data.some(
                      (row) => (row.plan_counts[planName] ?? 0) > 0,
                    );
                    const lineOpacity = hasEnrollments
                      ? isFocused
                        ? 1
                        : 0.2
                      : 0;

                    return (
                      <Line
                        className="mix-blend-multiply"
                        key={`line-${planName}-${String(index)}`}
                        type="monotone"
                        dataKey={`plan_counts.${planName}`}
                        stroke={CHART_COLORS[colorIndex].light}
                        strokeWidth={isFocused ? 5 : 4}
                        strokeOpacity={lineOpacity}
                        dot={{
                          fill: CHART_COLORS[colorIndex].dark,
                          opacity: hasEnrollments ? 1 : 0,
                          stroke: "none",
                          r: isFocused ? 5 : 4,
                        }}
                        activeDot={{
                          fill: CHART_COLORS[colorIndex].dark,
                          opacity: hasEnrollments ? 1 : 0,
                          stroke: "none",
                          r: isFocused ? 7 : 6,
                        }}
                        animationDuration={500}
                        animationEasing="ease-out"
                        isAnimationActive={true}
                        onClick={() => {
                          setFocusedLine(
                            focusedLine === planName ? null : planName,
                          );
                        }}
                        style={{ cursor: "pointer" }}
                      />
                    );
                  });
              })()}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </ChartCard>
  );
}
